import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, X, Cookie } from '@phosphor-icons/react';
import { useLanguage } from './i18n';
import DOMPurify from 'dompurify';

// Legal page content
const LEGAL_CONTENT = {
  disclaimer: {
    en: {
      title: "Disclaimer",
      content: `
**Entertainment Purpose Only**

All services provided by ONIMA, including but not limited to tarot readings, horoscopes, numerology calculations, and AI-guided conversations, are intended solely for entertainment purposes.

**No Professional Advice**

The information provided through ONIMA does not constitute professional advice of any kind, including but not limited to:
- Medical or health advice
- Financial or investment advice
- Legal advice
- Psychological or therapeutic advice

**No Guarantees**

ONIMA makes no claims, promises, or guarantees about the accuracy, completeness, or adequacy of the contents of this service. We do not warrant that the service will be accurate or reliable.

**Personal Responsibility**

Users are solely responsible for their own decisions and actions. Any reliance you place on information from ONIMA is strictly at your own risk.

**Seek Professional Help**

If you are experiencing serious personal, emotional, financial, or health issues, please consult with qualified professionals.

**Age Requirement**

This service is intended for users aged 18 and older.

Last updated: February 2025
      `
    },
    ru: {
      title: "Отказ от ответственности",
      content: `
**Только для развлечения**

Все услуги, предоставляемые ONIMA, включая, но не ограничиваясь, гадания на картах Таро, гороскопы, нумерологические расчеты и беседы с ИИ-гидом, предназначены исключительно для развлекательных целей.

**Не является профессиональной консультацией**

Информация, предоставляемая через ONIMA, не является профессиональной консультацией любого рода, включая:
- Медицинские консультации
- Финансовые или инвестиционные консультации
- Юридические консультации
- Психологические или терапевтические консультации

**Отсутствие гарантий**

ONIMA не дает никаких заявлений, обещаний или гарантий относительно точности, полноты или адекватности содержания данного сервиса.

**Личная ответственность**

Пользователи несут полную ответственность за свои решения и действия. Любое доверие к информации от ONIMA осуществляется исключительно на ваш собственный риск.

**Обращайтесь к специалистам**

Если вы испытываете серьезные личные, эмоциональные, финансовые или проблемы со здоровьем, пожалуйста, обратитесь к квалифицированным специалистам.

**Возрастное ограничение**

Этот сервис предназначен для пользователей от 18 лет.

Последнее обновление: Февраль 2025
      `
    },
    uk: {
      title: "Відмова від відповідальності",
      content: `
**Лише для розваги**

Усі послуги, що надаються ONIMA, включаючи, але не обмежуючись, ворожіння на картах Таро, гороскопи, нумерологічні розрахунки та бесіди з ІІ-гідом, призначені виключно для розважальних цілей.

**Не є професійною консультацією**

Інформація, що надається через ONIMA, не є професійною консультацією будь-якого роду, включаючи:
- Медичні консультації
- Фінансові або інвестиційні консультації
- Юридичні консультації
- Психологічні або терапевтичні консультації

**Відсутність гарантій**

ONIMA не надає жодних заяв, обіцянок або гарантій щодо точності, повноти або адекватності вмісту цього сервісу.

**Особиста відповідальність**

Користувачі несуть повну відповідальність за свої рішення та дії. Будь-яка довіра до інформації від ONIMA здійснюється виключно на ваш власний ризик.

**Звертайтеся до спеціалістів**

Якщо ви відчуваєте серйозні особисті, емоційні, фінансові проблеми або проблеми зі здоров'ям, будь ласка, зверніться до кваліфікованих спеціалістів.

**Вікове обмеження**

Цей сервіс призначений для користувачів від 18 років.

Останнє оновлення: Лютий 2025
      `
    },
    de: {
      title: "Haftungsausschluss",
      content: `
**Nur zu Unterhaltungszwecken**

Alle von ONIMA angebotenen Dienste, einschließlich Tarot-Lesungen, Horoskope, Numerologie-Berechnungen und KI-gestützte Gespräche, dienen ausschließlich der Unterhaltung.

**Keine professionelle Beratung**

Die über ONIMA bereitgestellten Informationen stellen keine professionelle Beratung jeglicher Art dar, einschließlich:
- Medizinische Beratung
- Finanz- oder Anlageberatung
- Rechtsberatung
- Psychologische oder therapeutische Beratung

**Keine Garantien**

ONIMA gibt keine Zusicherungen, Versprechen oder Garantien bezüglich der Genauigkeit, Vollständigkeit oder Angemessenheit des Inhalts dieses Dienstes.

**Eigenverantwortung**

Die Nutzer sind allein für ihre eigenen Entscheidungen und Handlungen verantwortlich. Jedes Vertrauen in Informationen von ONIMA erfolgt ausschließlich auf eigenes Risiko.

**Professionelle Hilfe suchen**

Wenn Sie ernsthafte persönliche, emotionale, finanzielle oder gesundheitliche Probleme haben, wenden Sie sich bitte an qualifizierte Fachleute.

**Altersanforderung**

Dieser Dienst ist für Nutzer ab 18 Jahren bestimmt.

Letzte Aktualisierung: Februar 2025
      `
    }
  },
  privacy: {
    en: {
      title: "Privacy Policy",
      content: `
**Information We Collect**

When you use ONIMA, we may collect:
- Account information (email, name) when you register
- Birth date and location data for personalized readings
- Chat conversation history
- Usage data and analytics

**How We Use Your Information**

We use your information to:
- Provide personalized divination services
- Improve our AI responses and services
- Send service-related communications
- Process payments and credits

**Data Storage**

Your data is stored securely on cloud servers. Chat history and readings are associated with your account.

**Third-Party Services**

We use:
- Authentication providers (Google)
- AI services for generating responses
- Email services for communications
- Analytics for improving our service

**Your Rights**

You have the right to:
- Access your personal data
- Request data deletion
- Export your data
- Opt-out of marketing communications

**Contact**

For privacy-related inquiries: info@onima.cc

**Company Information**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine

Last updated: February 2026
      `
    },
    ru: {
      title: "Политика конфиденциальности",
      content: `
**Информация, которую мы собираем**

При использовании ONIMA мы можем собирать:
- Данные аккаунта (email, имя) при регистрации
- Дату рождения и данные о местоположении для персонализированных гаданий
- Историю переписки
- Данные об использовании и аналитику

**Как мы используем вашу информацию**

Мы используем вашу информацию для:
- Предоставления персонализированных услуг гадания
- Улучшения наших ИИ-ответов и услуг
- Отправки сервисных сообщений
- Обработки платежей и кредитов

**Хранение данных**

Ваши данные надежно хранятся на облачных серверах. История чатов и гаданий связана с вашим аккаунтом.

**Сторонние сервисы**

Мы используем:
- Провайдеры аутентификации (Google)
- ИИ-сервисы для генерации ответов
- Email-сервисы для коммуникаций
- Аналитику для улучшения сервиса

**Ваши права**

Вы имеете право на:
- Доступ к вашим персональным данным
- Запрос на удаление данных
- Экспорт ваших данных
- Отказ от маркетинговых рассылок

**Контакты**

По вопросам конфиденциальности: info@onima.cc

**Информация о компании**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine

Последнее обновление: Февраль 2026
      `
    },
    uk: {
      title: "Політика конфіденційності",
      content: `
**Інформація, яку ми збираємо**

При використанні ONIMA ми можемо збирати:
- Дані облікового запису (email, ім'я) при реєстрації
- Дату народження та дані про місцезнаходження для персоналізованих ворожінь
- Історію переписки
- Дані про використання та аналітику

**Як ми використовуємо вашу інформацію**

Ми використовуємо вашу інформацію для:
- Надання персоналізованих послуг ворожіння
- Покращення наших ІІ-відповідей та послуг
- Надсилання сервісних повідомлень
- Обробки платежів та кредитів

**Зберігання даних**

Ваші дані надійно зберігаються на хмарних серверах. Історія чатів та ворожінь пов'язана з вашим обліковим записом.

**Сторонні сервіси**

Ми використовуємо:
- Провайдери автентифікації (Google)
- ІІ-сервіси для генерації відповідей
- Email-сервіси для комунікацій
- Аналітику для покращення сервісу

**Ваші права**

Ви маєте право на:
- Доступ до ваших персональних даних
- Запит на видалення даних
- Експорт ваших даних
- Відмову від маркетингових розсилок

**Контакти**

З питань конфіденційності: info@onima.cc

**Інформація про компанію**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine

Останнє оновлення: Лютий 2026
      `
    },
    de: {
      title: "Datenschutzerklärung",
      content: `
**Informationen, die wir erheben**

Bei der Nutzung von ONIMA können wir folgende Daten erheben:
- Kontodaten (E-Mail, Name) bei der Registrierung
- Geburtsdatum und Standortdaten für personalisierte Lesungen
- Chat-Gesprächsverlauf
- Nutzungsdaten und Analysen

**Wie wir Ihre Informationen verwenden**

Wir verwenden Ihre Informationen für:
- Bereitstellung personalisierter Wahrsagedienste
- Verbesserung unserer KI-Antworten und Dienste
- Versand dienstbezogener Mitteilungen
- Verarbeitung von Zahlungen und Guthaben

**Datenspeicherung**

Ihre Daten werden sicher auf Cloud-Servern gespeichert. Chat-Verlauf und Lesungen sind mit Ihrem Konto verknüpft.

**Drittanbieterdienste**

Wir nutzen:
- Authentifizierungsanbieter (Google)
- KI-Dienste zur Generierung von Antworten
- E-Mail-Dienste für Kommunikation
- Analysen zur Verbesserung unseres Dienstes

**Ihre Rechte (DSGVO)**

Sie haben das Recht auf:
- Auskunft über Ihre personenbezogenen Daten (Art. 15 DSGVO)
- Berichtigung unrichtiger Daten (Art. 16 DSGVO)
- Löschung Ihrer Daten (Art. 17 DSGVO)
- Einschränkung der Verarbeitung (Art. 18 DSGVO)
- Datenübertragbarkeit (Art. 20 DSGVO)
- Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)
- Widerruf erteilter Einwilligungen (Art. 7 Abs. 3 DSGVO)

**Kontakt**

Für Datenschutzfragen: info@onima.cc

Letzte Aktualisierung: Februar 2026
      `
    }
  },
  terms: {
    en: {
      title: "Terms of Service",
      content: `
**1. Service Provider**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine
Email: info@onima.cc

**2. Service Description**

ONIMA provides AI-powered divination services including:
- Tarot card readings
- Horoscope interpretations
- Numerology calculations
- Personalized guidance

**3. Entertainment Disclaimer**

IMPORTANT: ONIMA is for entertainment purposes only.

Our services do not constitute professional advice in:
- Medical or health matters
- Legal matters
- Financial or investment decisions
- Psychological or mental health issues

Always consult qualified professionals for such matters.

**4. Account and Credits**

- Registration is free
- Free credits are provided upon registration
- Additional credits can be purchased
- Credits are non-refundable once used

**5. Pricing**

- Subscription: 5 EUR/month
- Credit packages: As displayed in the app

All prices include applicable taxes.

**6. Right of Withdrawal (EU Users)**

You have the right to withdraw from a purchase within 14 days without giving any reason.

Exception: Digital content that has been accessed or used is excluded from the right of withdrawal, as per your consent at the time of purchase.

To withdraw, contact: info@onima.cc

**7. User Conduct**

You agree not to:
- Use the service for illegal purposes
- Attempt to hack or disrupt the service
- Create multiple accounts to abuse free credits
- Share your account with others

**8. Intellectual Property**

All content, including AI-generated readings, is owned by ONIMA or its licensors. You may not reproduce or distribute content without permission.

**9. Limitation of Liability**

To the maximum extent permitted by law, ONIMA shall not be liable for any indirect, incidental, or consequential damages arising from your use of the service.

**10. Governing Law**

These terms are governed by the laws of Ukraine. For EU consumers, mandatory consumer protection laws of your country of residence apply.

**11. Contact**

Email: info@onima.cc

Last updated: February 2026
      `
    },
    ru: {
      title: "Условия использования",
      content: `
**1. Поставщик услуг**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine
Email: info@onima.cc

**2. Описание услуг**

ONIMA предоставляет услуги AI-гадания, включая:
- Расклады карт Таро
- Интерпретации гороскопов
- Нумерологические расчёты
- Персонализированные консультации

**3. Отказ от ответственности (развлечение)**

ВАЖНО: ONIMA предназначен исключительно для развлечения.

Наши услуги не являются профессиональной консультацией в:
- Медицинских или вопросах здоровья
- Юридических вопросах
- Финансовых или инвестиционных решениях
- Психологических или вопросах ментального здоровья

Всегда обращайтесь к квалифицированным специалистам.

**4. Аккаунт и кредиты**

- Регистрация бесплатна
- Бесплатные кредиты предоставляются при регистрации
- Дополнительные кредиты можно приобрести
- Кредиты не подлежат возврату после использования

**5. Цены**

- Подписка: 5 EUR/мес
- Пакеты кредитов: как указано в приложении

Все цены включают применимые налоги.

**6. Право на отзыв (пользователи ЕС)**

Вы имеете право отозвать покупку в течение 14 дней без объяснения причин.

Исключение: Цифровой контент, к которому был осуществлён доступ или который был использован, исключён из права на отзыв.

Для отзыва: info@onima.cc

**7. Правила поведения**

Вы соглашаетесь не:
- Использовать сервис в незаконных целях
- Пытаться взломать или нарушить работу сервиса
- Создавать множественные аккаунты для злоупотребления бесплатными кредитами
- Делиться аккаунтом с другими

**8. Интеллектуальная собственность**

Весь контент, включая AI-генерированные расклады, принадлежит ONIMA или его лицензиарам.

**9. Ограничение ответственности**

В максимальной степени, допустимой законом, ONIMA не несёт ответственности за косвенные, случайные или последующие убытки.

**10. Применимое право**

Настоящие условия регулируются законодательством Украины. Для потребителей ЕС применяется обязательное законодательство о защите прав потребителей вашей страны проживания.

**11. Контакт**

Email: info@onima.cc

Последнее обновление: Февраль 2026
      `
    },
    uk: {
      title: "Умови використання",
      content: `
**1. Постачальник послуг**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine
Email: info@onima.cc

**2. Опис послуг**

ONIMA надає послуги AI-ворожіння, включаючи:
- Розклади карт Таро
- Інтерпретації гороскопів
- Нумерологічні розрахунки
- Персоналізовані консультації

**3. Відмова від відповідальності (розвага)**

ВАЖЛИВО: ONIMA призначений виключно для розваги.

Наші послуги не є професійною консультацією в:
- Медичних питаннях або питаннях здоров'я
- Юридичних питаннях
- Фінансових або інвестиційних рішеннях
- Психологічних питаннях або питаннях ментального здоров'я

Завжди звертайтеся до кваліфікованих спеціалістів.

**4. Акаунт та кредити**

- Реєстрація безкоштовна
- Безкоштовні кредити надаються при реєстрації
- Додаткові кредити можна придбати
- Кредити не підлягають поверненню після використання

**5. Ціни**

- Підписка: 5 EUR/міс
- Пакети кредитів: як зазначено в додатку

Усі ціни включають застосовні податки.

**6. Право на відкликання (користувачі ЄС)**

Ви маєте право відкликати покупку протягом 14 днів без пояснення причин.

Виняток: Цифровий контент, до якого було здійснено доступ або який було використано, виключений з права на відкликання.

Для відкликання: info@onima.cc

**7. Правила поведінки**

Ви погоджуєтесь не:
- Використовувати сервіс у незаконних цілях
- Намагатися зламати або порушити роботу сервісу
- Створювати множинні акаунти для зловживання безкоштовними кредитами
- Ділитися акаунтом з іншими

**8. Інтелектуальна власність**

Увесь контент, включаючи AI-генеровані розклади, належить ONIMA або його ліцензіарам.

**9. Обмеження відповідальності**

В максимальній мірі, дозволеній законом, ONIMA не несе відповідальності за непрямі, випадкові або подальші збитки.

**10. Застосовне право**

Ці умови регулюються законодавством України. Для споживачів ЄС застосовується обов'язкове законодавство про захист прав споживачів вашої країни проживання.

**11. Контакт**

Email: info@onima.cc

Останнє оновлення: Лютий 2026
      `
    },
    de: {
      title: "Nutzungsbedingungen",
      content: `
**1. Dienstanbieter**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine
E-Mail: info@onima.cc

**2. Dienstbeschreibung**

ONIMA bietet KI-gestützte Wahrsagedienste, darunter:
- Tarot-Kartenlegungen
- Horoskop-Interpretationen
- Numerologie-Berechnungen
- Personalisierte Beratung

**3. Unterhaltungshinweis**

WICHTIG: ONIMA dient ausschließlich Unterhaltungszwecken.

Unsere Dienste stellen keine professionelle Beratung dar in:
- Medizinischen oder gesundheitlichen Angelegenheiten
- Rechtlichen Angelegenheiten
- Finanziellen oder Anlageentscheidungen
- Psychologischen oder psychischen Gesundheitsfragen

Konsultieren Sie stets qualifizierte Fachleute.

**4. Konto und Guthaben**

- Die Registrierung ist kostenlos
- Bei der Registrierung werden kostenlose Guthaben gewährt
- Zusätzliche Guthaben können erworben werden
- Guthaben sind nach Verwendung nicht erstattungsfähig

**5. Preise**

- Abonnement: 5 EUR/Monat
- Guthaben-Pakete: Wie in der App angezeigt

Alle Preise verstehen sich inklusive anfallender Steuern.

**6. Widerrufsrecht (EU-Nutzer)**

Sie haben das Recht, einen Kauf innerhalb von 14 Tagen ohne Angabe von Gründen zu widerrufen.

Ausnahme: Digitale Inhalte, auf die zugegriffen oder die genutzt wurden, sind vom Widerrufsrecht ausgeschlossen.

Zum Widerruf kontaktieren Sie: info@onima.cc

**7. Nutzerverhalten**

Sie verpflichten sich:
- Den Dienst nicht für illegale Zwecke zu nutzen
- Nicht zu versuchen, den Dienst zu hacken oder zu stören
- Keine mehrfachen Konten zur Ausnutzung kostenloser Guthaben zu erstellen
- Ihr Konto nicht mit anderen zu teilen

**8. Geistiges Eigentum**

Alle Inhalte, einschließlich KI-generierter Lesungen, gehören ONIMA oder seinen Lizenzgebern.

**9. Haftungsbeschränkung**

Soweit gesetzlich zulässig, haftet ONIMA nicht für indirekte, zufällige oder Folgeschäden.

**10. Anwendbares Recht**

Diese Bedingungen unterliegen dem Recht der Ukraine. Für EU-Verbraucher gelten die zwingenden Verbraucherschutzgesetze Ihres Wohnsitzlandes.

**11. Kontakt**

E-Mail: info@onima.cc

Letzte Aktualisierung: Februar 2026
      `
    }
  },
  contacts: {
    en: {
      title: "Contact Us",
      content: `
**Get in Touch**

We'd love to hear from you! Whether you have questions, feedback, or need support.

**Email**

General inquiries: support@onima.cc
Support: support@onima.cc

**Response Time**

We aim to respond to all inquiries within 24-48 hours.

**Company Information**

ONIMA

**Registered Address**


      `
    },
    ru: {
      title: "Контакты",
      content: `
**Свяжитесь с нами**

Мы будем рады услышать вас! Если у вас есть вопросы, отзывы или нужна поддержка.

**Email**

Общие вопросы: support@onima.cc
Поддержка: support@onima.cc

**Время ответа**

Мы стараемся отвечать на все запросы в течение 24-48 часов.

**Информация о компании**

ONIMA

**Юридический адрес**


      `
    },
    uk: {
      title: "Контакти",
      content: `
**Зв'яжіться з нами**

Ми будемо раді почути вас! Якщо у вас є питання, відгуки або потрібна підтримка.

**Email**

Загальні питання: support@onima.cc
Підтримка: support@onima.cc

**Час відповіді**

Ми намагаємося відповідати на всі запити протягом 24-48 годин.

**Інформація про компанію**

ONIMA

**Юридична адреса**


      `
    },
    de: {
      title: "Kontakt",
      content: `
**Kontaktieren Sie uns**

Wir freuen uns über Ihre Nachricht! Ob Fragen, Feedback oder Support-Anfragen.

**E-Mail**

Allgemeine Anfragen: support@onima.cc
Support: support@onima.cc

**Antwortzeit**

Wir bemühen uns, alle Anfragen innerhalb von 24-48 Stunden zu beantworten.
      `
    }
  },
  pricing: {
    en: {
      title: "Pricing",
      content: `
**Credit System**

ONIMA uses a credit system for all services. Credits never expire and can be used at any time.

**Service Costs**

<div class="pricing-table">
<table>
<thead><tr><th>Service</th><th>Credits</th></tr></thead>
<tbody>
<tr><td>Tarot Reading (3-card spread)</td><td>10 ✦</td></tr>
<tr><td>Personal Horoscope</td><td>10 ✦</td></tr>
<tr><td>Numerology Analysis</td><td>10 ✦</td></tr>
<tr><td>Oracle Conversation</td><td>1 ✦ per response</td></tr>
</tbody>
</table>
</div>

**Getting Credits**

- **Free credits** — New users receive 100 free credits upon registration
- **Referral program** — Invite friends and earn 50 credits for each registration
- **Credit packs** — Coming soon

**Questions?**

Contact us at support@onima.cc
      `
    },
    ru: {
      title: "Тарифы",
      content: `
**Система кредитов**

ONIMA использует систему кредитов для всех услуг. Кредиты не сгорают и могут использоваться в любое время.

**Стоимость услуг**

<div class="pricing-table">
<table>
<thead><tr><th>Услуга</th><th>Кредиты</th></tr></thead>
<tbody>
<tr><td>Расклад Таро (3 карты)</td><td>10 ✦</td></tr>
<tr><td>Персональный гороскоп</td><td>10 ✦</td></tr>
<tr><td>Нумерологический анализ</td><td>10 ✦</td></tr>
<tr><td>Диалог с Оракулом</td><td>1 ✦ за ответ</td></tr>
</tbody>
</table>
</div>

**Получение кредитов**

- **Бесплатные кредиты** — Новые пользователи получают 100 бесплатных кредитов при регистрации
- **Реферальная программа** — Приглашайте друзей и получайте 50 кредитов за каждую регистрацию
- **Пакеты кредитов** — Скоро

**Вопросы?**

Свяжитесь с нами: support@onima.cc
      `
    },
    uk: {
      title: "Тарифи",
      content: `
**Система кредитів**

ONIMA використовує систему кредитів для всіх послуг. Кредити не згорають і можуть використовуватися в будь-який час.

**Вартість послуг**

<div class="pricing-table">
<table>
<thead><tr><th>Послуга</th><th>Кредити</th></tr></thead>
<tbody>
<tr><td>Розклад Таро (3 карти)</td><td>10 ✦</td></tr>
<tr><td>Персональний гороскоп</td><td>10 ✦</td></tr>
<tr><td>Нумерологічний аналіз</td><td>10 ✦</td></tr>
<tr><td>Діалог з Оракулом</td><td>1 ✦ за відповідь</td></tr>
</tbody>
</table>
</div>

**Отримання кредитів**

- **Безкоштовні кредити** — Нові користувачі отримують 100 безкоштовних кредитів при реєстрації
- **Реферальна програма** — Запрошуйте друзів та отримуйте 50 кредитів за кожну реєстрацію
- **Пакети кредитів** — Незабаром

**Питання?**

Зв'яжіться з нами: support@onima.cc
      `
    },
    de: {
      title: "Preise",
      content: `
**Guthaben-System**

ONIMA verwendet ein Guthaben-System für alle Dienste. Guthaben verfallen nie und können jederzeit verwendet werden.

**Dienstkosten**

<div class="pricing-table">
<table>
<thead><tr><th>Dienst</th><th>Guthaben</th></tr></thead>
<tbody>
<tr><td>Tarot-Lesung (3 Karten)</td><td>10 ✦</td></tr>
<tr><td>Persönliches Horoskop</td><td>10 ✦</td></tr>
<tr><td>Numerologie-Analyse</td><td>10 ✦</td></tr>
<tr><td>Orakel-Gespräch</td><td>1 ✦ pro Antwort</td></tr>
</tbody>
</table>
</div>

**Guthaben erhalten**

- **Kostenloses Guthaben** — Neue Benutzer erhalten 100 kostenlose Guthaben bei der Registrierung
- **Empfehlungsprogramm** — Laden Sie Freunde ein und verdienen Sie 50 Guthaben pro Registrierung
- **Guthaben-Pakete** — Bald verfügbar

**Fragen?**

Kontaktieren Sie uns: support@onima.cc
      `
    }
  },
  impressum: {
    en: {
      title: "Impressum",
      content: `
**Angaben gemäß § 5 TMG**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv
Ukraine

**Kontakt**

E-Mail: info@onima.cc
Telefon: +380 66 216 09 32

**Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine

**EU-Streitschlichtung**

Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
https://ec.europa.eu/consumers/odr/

Unsere E-Mail-Adresse finden Sie oben im Impressum.

**Verbraucherstreitbeilegung**

Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.

**Haftungsausschluss**

Die Inhalte dieser Website dienen ausschließlich Unterhaltungszwecken. ONIMA bietet keine professionelle Beratung in rechtlichen, medizinischen, finanziellen oder psychologischen Angelegenheiten.

Stand: Februar 2026
      `
    },
    ru: {
      title: "Импрессум",
      content: `
**Angaben gemäß § 5 TMG**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv
Ukraine

**Kontakt**

E-Mail: info@onima.cc
Telefon: +380 66 216 09 32

**Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine

**EU-Streitschlichtung**

Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
https://ec.europa.eu/consumers/odr/

Unsere E-Mail-Adresse finden Sie oben im Impressum.

**Verbraucherstreitbeilegung**

Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.

**Haftungsausschluss**

Die Inhalte dieser Website dienen ausschließlich Unterhaltungszwecken. ONIMA bietet keine professionelle Beratung in rechtlichen, medizinischen, finanziellen oder psychologischen Angelegenheiten.

Stand: Februar 2026
      `
    },
    uk: {
      title: "Імпресум",
      content: `
**Angaben gemäß § 5 TMG**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv
Ukraine

**Kontakt**

E-Mail: info@onima.cc
Telefon: +380 66 216 09 32

**Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine

**EU-Streitschlichtung**

Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
https://ec.europa.eu/consumers/odr/

Unsere E-Mail-Adresse finden Sie oben im Impressum.

**Verbraucherstreitbeilegung**

Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.

**Haftungsausschluss**

Die Inhalte dieser Website dienen ausschließlich Unterhaltungszwecken. ONIMA bietet keine professionelle Beratung in rechtlichen, medizinischen, finanziellen oder psychologischen Angelegenheiten.

Stand: Februar 2026
      `
    },
    de: {
      title: "Impressum",
      content: `
**Angaben gemäß § 5 TMG**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv
Ukraine

**Kontakt**

E-Mail: info@onima.cc
Telefon: +380 66 216 09 32

**Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine

**EU-Streitschlichtung**

Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
https://ec.europa.eu/consumers/odr/

Unsere E-Mail-Adresse finden Sie oben im Impressum.

**Verbraucherstreitbeilegung**

Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.

**Haftungsausschluss**

Die Inhalte dieser Website dienen ausschließlich Unterhaltungszwecken. ONIMA bietet keine professionelle Beratung in rechtlichen, medizinischen, finanziellen oder psychologischen Angelegenheiten.

Stand: Februar 2026
      `
    }
  },
  datenschutz: {
    en: {
      title: "Datenschutzerklärung",
      content: `
**1. Verantwortlicher**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine

E-Mail: info@onima.cc
Telefon: +380 66 216 09 32

**2. Übersicht der Datenverarbeitung**

Wir verarbeiten personenbezogene Daten nur, soweit dies zur Bereitstellung unserer Dienste erforderlich ist.

**3. Erhobene Daten**

Bei der Registrierung:
- E-Mail-Adresse
- Name (optional)
- Passwort (verschlüsselt)

Bei der Nutzung:
- Geburtsdatum, -zeit und -ort (für personalisierte Horoskope)
- Chat-Verlauf und Lesungen
- Präferenzen und Einstellungen

Automatisch erfasst:
- IP-Adresse
- Browser-Typ und Version
- Betriebssystem
- Datum und Uhrzeit des Zugriffs

**4. Zweck der Verarbeitung**

Wir verwenden Ihre Daten für:
- Bereitstellung personalisierter Wahrsagedienste
- Verbesserung unserer KI-Antworten
- Abwicklung von Zahlungen und Krediten
- Kommunikation bezüglich Ihres Kontos

**5. Rechtsgrundlage**

Die Verarbeitung erfolgt auf Grundlage von:
- Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)
- Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
- Art. 6 Abs. 1 lit. f DSGVO (berechtigte Interessen)

**6. Speicherdauer**

- Kontodaten: Solange Ihr Konto aktiv ist
- Nach Kontolöschung: 30 Tage
- Zahlungsdaten: Gemäß gesetzlicher Aufbewahrungspflichten

**7. Ihre Rechte**

Sie haben das Recht auf:
- Auskunft (Art. 15 DSGVO)
- Berichtigung (Art. 16 DSGVO)
- Löschung (Art. 17 DSGVO)
- Einschränkung der Verarbeitung (Art. 18 DSGVO)
- Datenübertragbarkeit (Art. 20 DSGVO)
- Widerspruch (Art. 21 DSGVO)

Zur Ausübung Ihrer Rechte kontaktieren Sie uns unter: info@onima.cc

**8. Cookies**

Wir verwenden:

Notwendige Cookies:
- Sitzungscookies für Authentifizierung
- Präferenz-Cookies für Spracheinstellungen

Analyse-Cookies (nur mit Ihrer Einwilligung):
- Zur Verbesserung unserer Dienste

Sie können Cookies in Ihren Browsereinstellungen verwalten.

**9. Drittanbieter**

Wir nutzen folgende Dienste:
- Google (Authentifizierung)
- Stripe (Zahlungsabwicklung)
- OpenAI (KI-Dienste)
- MongoDB (Datenspeicherung)

**10. Datensicherheit**

Wir setzen technische und organisatorische Maßnahmen ein:
- SSL/TLS-Verschlüsselung
- Verschlüsselte Passwörter
- Regelmäßige Sicherheitsupdates

**11. Kontakt für Datenschutzanfragen**

E-Mail: info@onima.cc

Stand: Februar 2026
      `
    },
    ru: {
      title: "Datenschutzerklärung",
      content: `
**1. Verantwortlicher**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine

E-Mail: info@onima.cc
Telefon: +380 66 216 09 32

**2. Übersicht der Datenverarbeitung**

Wir verarbeiten personenbezogene Daten nur, soweit dies zur Bereitstellung unserer Dienste erforderlich ist.

**3. Erhobene Daten**

Bei der Registrierung:
- E-Mail-Adresse
- Name (optional)
- Passwort (verschlüsselt)

Bei der Nutzung:
- Geburtsdatum, -zeit und -ort (für personalisierte Horoskope)
- Chat-Verlauf und Lesungen
- Präferenzen und Einstellungen

Automatisch erfasst:
- IP-Adresse
- Browser-Typ und Version
- Betriebssystem
- Datum und Uhrzeit des Zugriffs

**4. Zweck der Verarbeitung**

Wir verwenden Ihre Daten für:
- Bereitstellung personalisierter Wahrsagedienste
- Verbesserung unserer KI-Antworten
- Abwicklung von Zahlungen und Krediten
- Kommunikation bezüglich Ihres Kontos

**5. Rechtsgrundlage**

Die Verarbeitung erfolgt auf Grundlage von:
- Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)
- Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
- Art. 6 Abs. 1 lit. f DSGVO (berechtigte Interessen)

**6. Speicherdauer**

- Kontodaten: Solange Ihr Konto aktiv ist
- Nach Kontolöschung: 30 Tage
- Zahlungsdaten: Gemäß gesetzlicher Aufbewahrungspflichten

**7. Ihre Rechte**

Sie haben das Recht auf:
- Auskunft (Art. 15 DSGVO)
- Berichtigung (Art. 16 DSGVO)
- Löschung (Art. 17 DSGVO)
- Einschränkung der Verarbeitung (Art. 18 DSGVO)
- Datenübertragbarkeit (Art. 20 DSGVO)
- Widerspruch (Art. 21 DSGVO)

Zur Ausübung Ihrer Rechte kontaktieren Sie uns unter: info@onima.cc

**8. Cookies**

Wir verwenden:

Notwendige Cookies:
- Sitzungscookies für Authentifizierung
- Präferenz-Cookies für Spracheinstellungen

Analyse-Cookies (nur mit Ihrer Einwilligung):
- Zur Verbesserung unserer Dienste

Sie können Cookies in Ihren Browsereinstellungen verwalten.

**9. Drittanbieter**

Wir nutzen folgende Dienste:
- Google (Authentifizierung)
- Stripe (Zahlungsabwicklung)
- OpenAI (KI-Dienste)
- MongoDB (Datenspeicherung)

**10. Datensicherheit**

Wir setzen technische und organisatorische Maßnahmen ein:
- SSL/TLS-Verschlüsselung
- Verschlüsselte Passwörter
- Regelmäßige Sicherheitsupdates

**11. Kontakt für Datenschutzanfragen**

E-Mail: info@onima.cc

Stand: Februar 2026
      `
    },
    uk: {
      title: "Datenschutzerklärung",
      content: `
**1. Verantwortlicher**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine

E-Mail: info@onima.cc
Telefon: +380 66 216 09 32

**2. Übersicht der Datenverarbeitung**

Wir verarbeiten personenbezogene Daten nur, soweit dies zur Bereitstellung unserer Dienste erforderlich ist.

**3. Erhobene Daten**

Bei der Registrierung:
- E-Mail-Adresse
- Name (optional)
- Passwort (verschlüsselt)

Bei der Nutzung:
- Geburtsdatum, -zeit und -ort (für personalisierte Horoskope)
- Chat-Verlauf und Lesungen
- Präferenzen und Einstellungen

Automatisch erfasst:
- IP-Adresse
- Browser-Typ und Version
- Betriebssystem
- Datum und Uhrzeit des Zugriffs

**4. Zweck der Verarbeitung**

Wir verwenden Ihre Daten für:
- Bereitstellung personalisierter Wahrsagedienste
- Verbesserung unserer KI-Antworten
- Abwicklung von Zahlungen und Krediten
- Kommunikation bezüglich Ihres Kontos

**5. Rechtsgrundlage**

Die Verarbeitung erfolgt auf Grundlage von:
- Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)
- Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
- Art. 6 Abs. 1 lit. f DSGVO (berechtigte Interessen)

**6. Speicherdauer**

- Kontodaten: Solange Ihr Konto aktiv ist
- Nach Kontolöschung: 30 Tage
- Zahlungsdaten: Gemäß gesetzlicher Aufbewahrungspflichten

**7. Ihre Rechte**

Sie haben das Recht auf:
- Auskunft (Art. 15 DSGVO)
- Berichtigung (Art. 16 DSGVO)
- Löschung (Art. 17 DSGVO)
- Einschränkung der Verarbeitung (Art. 18 DSGVO)
- Datenübertragbarkeit (Art. 20 DSGVO)
- Widerspruch (Art. 21 DSGVO)

Zur Ausübung Ihrer Rechte kontaktieren Sie uns unter: info@onima.cc

**8. Cookies**

Wir verwenden:

Notwendige Cookies:
- Sitzungscookies für Authentifizierung
- Präferenz-Cookies für Spracheinstellungen

Analyse-Cookies (nur mit Ihrer Einwilligung):
- Zur Verbesserung unserer Dienste

Sie können Cookies in Ihren Browsereinstellungen verwalten.

**9. Drittanbieter**

Wir nutzen folgende Dienste:
- Google (Authentifizierung)
- Stripe (Zahlungsabwicklung)
- OpenAI (KI-Dienste)
- MongoDB (Datenspeicherung)

**10. Datensicherheit**

Wir setzen technische und organisatorische Maßnahmen ein:
- SSL/TLS-Verschlüsselung
- Verschlüsselte Passwörter
- Regelmäßige Sicherheitsupdates

**11. Kontakt für Datenschutzanfragen**

E-Mail: info@onima.cc

Stand: Februar 2026
      `
    },
    de: {
      title: "Datenschutzerklärung",
      content: `
**1. Verantwortlicher**

Dmytro Medviedev
Av. Antonova 14, App. 80
Kyiv, Ukraine

E-Mail: info@onima.cc
Telefon: +380 66 216 09 32

**2. Übersicht der Datenverarbeitung**

Wir verarbeiten personenbezogene Daten nur, soweit dies zur Bereitstellung unserer Dienste erforderlich ist.

**3. Erhobene Daten**

Bei der Registrierung:
- E-Mail-Adresse
- Name (optional)
- Passwort (verschlüsselt)

Bei der Nutzung:
- Geburtsdatum, -zeit und -ort (für personalisierte Horoskope)
- Chat-Verlauf und Lesungen
- Präferenzen und Einstellungen

Automatisch erfasst:
- IP-Adresse
- Browser-Typ und Version
- Betriebssystem
- Datum und Uhrzeit des Zugriffs

**4. Zweck der Verarbeitung**

Wir verwenden Ihre Daten für:
- Bereitstellung personalisierter Wahrsagedienste
- Verbesserung unserer KI-Antworten
- Abwicklung von Zahlungen und Krediten
- Kommunikation bezüglich Ihres Kontos

**5. Rechtsgrundlage**

Die Verarbeitung erfolgt auf Grundlage von:
- Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)
- Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
- Art. 6 Abs. 1 lit. f DSGVO (berechtigte Interessen)

**6. Speicherdauer**

- Kontodaten: Solange Ihr Konto aktiv ist
- Nach Kontolöschung: 30 Tage
- Zahlungsdaten: Gemäß gesetzlicher Aufbewahrungspflichten

**7. Ihre Rechte**

Sie haben das Recht auf:
- Auskunft (Art. 15 DSGVO)
- Berichtigung (Art. 16 DSGVO)
- Löschung (Art. 17 DSGVO)
- Einschränkung der Verarbeitung (Art. 18 DSGVO)
- Datenübertragbarkeit (Art. 20 DSGVO)
- Widerspruch (Art. 21 DSGVO)

Zur Ausübung Ihrer Rechte kontaktieren Sie uns unter: info@onima.cc

**8. Cookies**

Wir verwenden:

Notwendige Cookies:
- Sitzungscookies für Authentifizierung
- Präferenz-Cookies für Spracheinstellungen

Analyse-Cookies (nur mit Ihrer Einwilligung):
- Zur Verbesserung unserer Dienste

Sie können Cookies in Ihren Browsereinstellungen verwalten.

**9. Drittanbieter**

Wir nutzen folgende Dienste:
- Google (Authentifizierung)
- Stripe (Zahlungsabwicklung)
- OpenAI (KI-Dienste)
- MongoDB (Datenspeicherung)

**10. Datensicherheit**

Wir setzen technische und organisatorische Maßnahmen ein:
- SSL/TLS-Verschlüsselung
- Verschlüsselte Passwörter
- Regelmäßige Sicherheitsupdates

**11. Kontakt für Datenschutzanfragen**

E-Mail: info@onima.cc

Stand: Februar 2026
      `
    }
  }
};

// Footer translations
const FOOTER_TEXT = {
  en: {
    disclaimer: "Disclaimer",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    contacts: "Contact Us",
    pricing: "Pricing",
    impressum: "Impressum",
    datenschutz: "Datenschutz",
    copyright: "ONIMA. All rights reserved.",
    entertainment: "For entertainment purposes only. Not professional advice.",
    about: "About",
    legal: "Legal",
    services: "Services",
    social: "Social",
    aboutText: "AI-powered oracle for personal guidance through Tarot, Horoscopes, and Numerology.",
    telegram: "Telegram Channel",
    email: "Email",
    ageRestriction: "18+ only",
    tarotLink: "Tarot",
    horoscopeLink: "Horoscope",
    numerologyLink: "Numerology"
  },
  ru: {
    disclaimer: "Отказ от ответственности",
    privacy: "Конфиденциальность",
    terms: "Условия использования",
    contacts: "Контакты",
    pricing: "Тарифы",
    impressum: "Impressum",
    datenschutz: "Datenschutz",
    copyright: "ONIMA. Все права защищены.",
    entertainment: "Только для развлечения. Не является профессиональной консультацией.",
    about: "О нас",
    legal: "Юридическая информация",
    services: "Услуги",
    social: "Социальные сети",
    aboutText: "ИИ-оракул для персональных консультаций через Таро, Гороскопы и Нумерологию.",
    telegram: "Telegram канал",
    email: "Эл. почта",
    ageRestriction: "Только 18+",
    tarotLink: "Таро",
    horoscopeLink: "Гороскоп",
    numerologyLink: "Нумерология"
  },
  uk: {
    disclaimer: "Відмова від відповідальності",
    privacy: "Конфіденційність",
    terms: "Умови використання",
    contacts: "Контакти",
    pricing: "Тарифи",
    impressum: "Impressum",
    datenschutz: "Datenschutz",
    copyright: "ONIMA. Усі права захищені.",
    entertainment: "Лише для розваги. Не є професійною консультацією.",
    about: "Про нас",
    legal: "Юридична інформація",
    services: "Послуги",
    social: "Соціальні мережі",
    aboutText: "ІІ-оракул для персональних консультацій через Таро, Гороскопи та Нумерологію.",
    telegram: "Telegram канал",
    email: "Ел. пошта",
    ageRestriction: "Лише 18+",
    tarotLink: "Таро",
    horoscopeLink: "Гороскоп",
    numerologyLink: "Нумерологія"
  },
  de: {
    disclaimer: "Haftungsausschluss",
    privacy: "Datenschutz (EN)",
    terms: "Nutzungsbedingungen",
    contacts: "Kontakt",
    pricing: "Preise",
    impressum: "Impressum",
    datenschutz: "Datenschutz",
    copyright: "ONIMA. Alle Rechte vorbehalten.",
    entertainment: "Nur zu Unterhaltungszwecken. Keine professionelle Beratung.",
    about: "Über uns",
    legal: "Rechtliches",
    services: "Dienste",
    social: "Soziale Medien",
    aboutText: "KI-gestütztes Orakel für persönliche Beratung durch Tarot, Horoskope und Numerologie.",
    telegram: "Telegram-Kanal",
    email: "E-Mail",
    ageRestriction: "Nur ab 18",
    tarotLink: "Tarot",
    horoscopeLink: "Horoskop",
    numerologyLink: "Numerologie"
  }
};

// Cookie consent translations
const COOKIE_TEXT = {
  en: {
    message: "We use cookies to improve your experience.",
    accept: "Accept",
    decline: "Decline",
    learnMore: "Learn more"
  },
  ru: {
    message: "Мы используем cookies для улучшения вашего опыта.",
    accept: "Принять",
    decline: "Отклонить",
    learnMore: "Подробнее"
  },
  uk: {
    message: "Ми використовуємо cookies для покращення вашого досвіду.",
    accept: "Прийняти",
    decline: "Відхилити",
    learnMore: "Детальніше"
  },
  de: {
    message: "Wir verwenden Cookies, um Ihre Erfahrung zu verbessern.",
    accept: "Akzeptieren",
    decline: "Ablehnen",
    learnMore: "Mehr erfahren"
  }
};

// Page title mapping
const PAGE_TITLES = {
  impressum: 'Impressum | ONIMA',
  datenschutz: 'Datenschutz | ONIMA',
  privacy: 'Privacy Policy | ONIMA',
  terms: 'Terms of Service | ONIMA',
  disclaimer: 'Disclaimer | ONIMA',
  contacts: 'Contact Us | ONIMA',
  pricing: 'Pricing | ONIMA',
};

// Legal Page Component
export const LegalPage = ({ type }) => {
  const { language } = useLanguage();
  const content = LEGAL_CONTENT[type]?.[language] || LEGAL_CONTENT[type]?.en;
  const pageTitle = content ? (PAGE_TITLES[type] || `${content.title} | ONIMA`) : 'ONIMA';

  useEffect(() => {
    document.title = pageTitle;
    return () => { document.title = 'ONIMA'; };
  }, [pageTitle]);

  if (!content) return <div>Page not found</div>;

  // Simple markdown-like parsing with HTML support
  const parseContent = (text) => {
    const lines = text.split('\n');
    const result = [];
    let htmlBlock = '';
    let inHtmlBlock = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check for HTML blocks (like pricing table)
      if (line.includes('<div class="pricing-table">')) {
        inHtmlBlock = true;
        htmlBlock = '';
      }
      
      if (inHtmlBlock) {
        htmlBlock += line + '\n';
        if (line.includes('</div>')) {
          inHtmlBlock = false;
          result.push(
            <div 
              key={`html-${i}`} 
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlBlock) }} 
            />
          );
          htmlBlock = '';
        }
        continue;
      }
      
      // Regular markdown parsing
      if (line.startsWith('**') && line.endsWith('**')) {
        result.push(<h3 key={i} className="text-lg font-semibold text-secondary mt-6 mb-3">{line.slice(2, -2)}</h3>);
      } else if (line.startsWith('- ')) {
        // Parse bold text within list items
        const listContent = line.slice(2).replace(/\*\*([^*]+)\*\*/g, '<strong class="text-foreground">$1</strong>');
        result.push(
          <li 
            key={i} 
            className="ml-4 text-muted-foreground list-disc" 
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(listContent) }}
          />
        );
      } else if (line.trim() === '') {
        result.push(<br key={i} />);
      } else {
        result.push(<p key={i} className="text-muted-foreground mb-2">{line}</p>);
      }
    }
    
    return result;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-background py-8 px-4"
    >
      <div className="max-w-3xl mx-auto">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-secondary transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </Link>
        
        <h1 className="text-3xl font-heading text-secondary mb-8">{content.title}</h1>
        
        <div className="prose prose-invert max-w-none">
          {parseContent(content.content)}
        </div>
      </div>
    </motion.div>
  );
};

// Footer Component — EU-standard multi-column layout
export const Footer = () => {
  const { language } = useLanguage();
  const t = FOOTER_TEXT[language] || FOOTER_TEXT.en;
  const currentYear = new Date().getFullYear();

  return (
    <footer className="mt-auto border-t border-secondary/10 bg-[#060610]/95 backdrop-blur-xl relative z-20" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Column 1: About */}
          <div>
            <h4 className="font-heading text-sm tracking-widest uppercase text-secondary mb-4">{t.about}</h4>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3">
              {t.aboutText}
            </p>
            <span className="inline-block text-[10px] px-2 py-0.5 rounded border border-secondary/30 text-secondary/80">
              {t.ageRestriction}
            </span>
          </div>

          {/* Column 2: Legal */}
          <div>
            <h4 className="font-heading text-sm tracking-widest uppercase text-secondary mb-4">{t.legal}</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/impressum" className="text-xs text-muted-foreground hover:text-secondary transition-colors" data-testid="footer-impressum">
                  {t.impressum}
                </Link>
              </li>
              <li>
                <Link to="/datenschutz" className="text-xs text-muted-foreground hover:text-secondary transition-colors" data-testid="footer-datenschutz">
                  {t.datenschutz}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-xs text-muted-foreground hover:text-secondary transition-colors" data-testid="footer-privacy">
                  {t.privacy}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-xs text-muted-foreground hover:text-secondary transition-colors" data-testid="footer-terms">
                  {t.terms}
                </Link>
              </li>
              <li>
                <Link to="/disclaimer" className="text-xs text-muted-foreground hover:text-secondary transition-colors" data-testid="footer-disclaimer">
                  {t.disclaimer}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Services */}
          <div>
            <h4 className="font-heading text-sm tracking-widest uppercase text-secondary mb-4">{t.services}</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/pricing" className="text-xs text-muted-foreground hover:text-secondary transition-colors" data-testid="footer-pricing">
                  {t.pricing}
                </Link>
              </li>
              <li>
                <Link to="/" className="text-xs text-muted-foreground hover:text-secondary transition-colors">
                  {t.tarotLink}
                </Link>
              </li>
              <li>
                <Link to="/" className="text-xs text-muted-foreground hover:text-secondary transition-colors">
                  {t.horoscopeLink}
                </Link>
              </li>
              <li>
                <Link to="/" className="text-xs text-muted-foreground hover:text-secondary transition-colors">
                  {t.numerologyLink}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact & Social */}
          <div>
            <h4 className="font-heading text-sm tracking-widest uppercase text-secondary mb-4">{t.contacts}</h4>
            <ul className="space-y-2">
              <li>
                <a href="mailto:support@onima.cc" className="text-xs text-muted-foreground hover:text-secondary transition-colors flex items-center gap-1.5" data-testid="footer-email">
                  {t.email}: support@onima.cc
                </a>
              </li>
              <li>
                <a href="https://t.me/UA_orakul" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-secondary transition-colors flex items-center gap-1.5" data-testid="footer-telegram">
                  {t.telegram}: @UA_orakul
                </a>
              </li>
              <li>
                <Link to="/contacts" className="text-xs text-muted-foreground hover:text-secondary transition-colors" data-testid="footer-contacts">
                  {t.contacts}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-secondary/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground/60">
            &copy; {currentYear} {t.copyright}
          </p>
          <p className="text-[10px] text-muted-foreground/50 text-center sm:text-right">
            {t.entertainment}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Cookie Consent Banner
export const CookieConsent = () => {
  const { language } = useLanguage();
  const t = COOKIE_TEXT[language] || COOKIE_TEXT.en;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookie_consent', 'declined');
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-[999999] p-4"
        >
          <div className="max-w-4xl mx-auto bg-[var(--color-card)] backdrop-blur-xl border border-secondary/20 rounded-2xl p-4 shadow-2xl">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Cookie size={24} className="text-secondary shrink-0" />
              <p className="text-sm text-muted-foreground flex-1 text-center sm:text-left">
                {t.message}{' '}
                <Link to="/privacy" className="text-secondary hover:underline">
                  {t.learnMore}
                </Link>
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleDecline}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t.decline}
                </button>
                <button
                  onClick={handleAccept}
                  className="px-4 py-2 text-sm bg-secondary/20 hover:bg-secondary/30 text-secondary rounded-lg transition-colors"
                >
                  {t.accept}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Mini disclaimer for divination results
export const DivinationDisclaimer = () => {
  const { language } = useLanguage();
  const text = {
    en: "For entertainment purposes only. Not professional advice.",
    ru: "Только для развлечения. Не является профессиональной консультацией.",
    uk: "Лише для розваги. Не є професійною консультацією."
  };
  
  return (
    <p className="text-center text-xs text-muted-foreground/40 mt-4">
      {text[language] || text.en}
    </p>
  );
};

export default { LegalPage, Footer, CookieConsent, DivinationDisclaimer };
